import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';

import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';
import CICWorkflowQueue from 'components/WorkflowQueues/CICWorkflowQueue';
import LoadingWrapper from 'components/Common/LoadingWrapper';
import WorkflowStages from 'common/Workflows/WorkflowStages';

import {
  getStorageSetting,
  getWorkflowQueue,
  getWorkflowQueues,
  getUserQueue,
  takeQueueItem,
  getRtks,
  showOwnershipModal,
  getRtkXls,
  showRtkModal
} from 'actions';

const WAIT_INTERVAL = 1000;
const ENTER_KEY = 13;

class WorkflowQueuesSection extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    settings: PropTypes.shape({}),
    permissions: PropTypes.arrayOf(PropTypes.string),
    workflowType: PropTypes.oneOf(['form', 'cic']).isRequired,
    isFetchingQueues: PropTypes.bool,
    userQueue: PropTypes.shape({}),
    initialReviewQueue: PropTypes.shape({}),
    secondaryReviewQueue: PropTypes.shape({}),
    expertReviewQueue: PropTypes.shape({}),
    allQueue: PropTypes.shape({}),
    isItemShown: PropTypes.bool,
    isGettingXls: PropTypes.bool
  };

  static defaultProps = {
    settings: null,
    permissions: null,
    isFetchingQueues: false,
    userQueue: null,
    initialReviewQueue: null,
    secondaryReviewQueue: null,
    expertReviewQueue: null,
    allQueue: null,
    isItemShown: false,
    isGettingXls: false,
  };

  static isUserOwner(user) {
    const loggedOnUser = getStorageSetting('username');

    return (loggedOnUser != null && user != null && loggedOnUser === user);
  }

  constructor(props) {
    super(props);

    let isInitialReviewer = false;
    let isSecondaryReviewer = false;
    let isExpertReviewer = false;
    let isReviewer = false;

    if (props.permissions) {
      switch (props.workflowType) {
        case 'cic':
          isInitialReviewer = AccessControl.hasPermission(props.permissions, [ActivityPerms.cicwfInitialReviewer]);
          isSecondaryReviewer = AccessControl.hasPermission(props.permissions, [ActivityPerms.cicwfSecondaryReviewer]);
          isExpertReviewer = AccessControl.hasPermission(props.permissions, [ActivityPerms.cicwfExpertReviewer]);
          isReviewer = (isInitialReviewer || isSecondaryReviewer || isExpertReviewer);
          break;
        default:
          break;
      }
    }

    this.state = {
      pageNumber: 1,
      pageSize: 50,
      sortBy: null,
      sortDirection: null,
      filters: null,
      searchQuery: '',
      activeTab: 0,
      tabs: [
        { title: 'My Work', index: 0, enabled: true, visible: true },
        { title: 'Initial Review', stage: 'pendingreview', index: 1, enabled: isInitialReviewer, visible: false },
        { title: 'Secondary Review', stage: 'pendingsecondaryreview', index: 2, enabled: isSecondaryReviewer, visible: false },
        { title: 'Manager Review', stage: 'pendingexpertreview', index: 3, enabled: isExpertReviewer, visible: false },
        { title: 'All', stage: 'all', index: 4, enabled: isReviewer, visible: false }
      ],
    };
  }

  componentDidMount() {
    const { dispatch, workflowType } = this.props;
    const { pageNumber, pageSize } = this.state;

    dispatch(getWorkflowQueues(workflowType, pageNumber, pageSize));
  }

  componentDidUpdate(oldProps) {
    const { dispatch, permissions, workflowType, isItemShown } = this.props;
    const { activeTab, tabs, pageNumber, pageSize, sortBy, sortDirection, filters, searchQuery } = this.state;

    if (oldProps.permissions !== permissions) {
      if (permissions) {
        switch (workflowType) {
          case 'cic':
            tabs[1].enabled = AccessControl.hasPermission(permissions, [ActivityPerms.cicwfInitialReviewer]);
            tabs[2].enabled = AccessControl.hasPermission(permissions, [ActivityPerms.cicwfSecondaryReviewer]);
            tabs[3].enabled = AccessControl.hasPermission(permissions, [ActivityPerms.cicwfExpertReviewer]);
            tabs[4].enabled = (tabs[1].enabled || tabs[2].enabled || tabs[3].enabled);
            break;
          default:
            break;
        }
      }

      // eslint-disable-next-line
      this.setState({ tabs });
    }

    if (oldProps.isItemShown && !isItemShown) {
      if (activeTab === 0) {
        dispatch(getUserQueue(workflowType, pageNumber, pageSize, searchQuery));
      } else if (tabs[activeTab].stage === 'all') {
        switch (workflowType) {
          case 'cic':
            dispatch(getRtks(pageNumber, pageSize, sortBy, sortDirection, filters, searchQuery));
            break;
          default:
            break;
        }
      } else {
        dispatch(getWorkflowQueue(workflowType, pageNumber, pageSize, tabs[activeTab].stage, searchQuery));
      }
    }
  }

  onSelectTab(tabIndex) {
    const { dispatch, workflowType } = this.props;
    const { tabs, pageSize, sortBy, sortDirection, filters, searchQuery, activeTab } = this.state;

    if (tabIndex !== activeTab) {
      Object.keys(tabs).forEach((index) => {
        if (index != null) {
          tabs[index].visible = (tabs[index].index === tabIndex);
        }
      });
      this.setState({
        pageNumber: 1,
        activeTab: tabIndex,
        tabs
      });
    }

    if (tabIndex === 0) {
      dispatch(getUserQueue(workflowType, 1, pageSize, searchQuery));
    } else if (tabs[tabIndex].stage === 'all') {
      switch (workflowType) {
        case 'cic':
          dispatch(getRtks(1, pageSize, sortBy, sortDirection, filters, searchQuery));
          break;
        default:
          break;
      }
    } else {
      dispatch(getWorkflowQueue(workflowType, 1, pageSize, tabs[tabIndex].stage, searchQuery));
    }
  }

  onPageChange = (paging, sortModel, filters) => {
    const { dispatch, workflowType } = this.props;
    const { activeTab, tabs, searchQuery } = this.state;

    const sortBy = sortModel != null && sortModel[0] != null ? sortModel[0].colId : '';
    const sortDirection = sortModel != null && sortModel[0] != null ? sortModel[0].sort : '';

    this.setState({
      pageNumber: paging.number,
      pageSize: paging.size,
      sortBy,
      sortDirection,
      filters,
    });

    if (activeTab === 0) {
      dispatch(getUserQueue(workflowType, paging.number, paging.size, searchQuery));
    } else if (tabs[activeTab].stage === 'all') {
      switch (workflowType) {
        case 'cic':
          dispatch(getRtks(paging.number, paging.size, sortBy, sortDirection, filters, searchQuery));
          break;
        default:
          break;
      }
    } else {
      dispatch(getWorkflowQueue(workflowType, paging.number, paging.size, tabs[activeTab].stage, searchQuery));
    }
    return true;
  }

  onTakeQueueItem(item) {
    const { activeTab } = this.state;
    const { dispatch, permissions, workflowType } = this.props;

    const isPendingStage = item.workflowStage === WorkflowStages.PendingReview || item.workflowStage === WorkflowStages.PendingSecondaryReview || item.workflowStage === WorkflowStages.PendingExpertReview;

    if ((activeTab === 0 && item.id != null) || WorkflowQueuesSection.isUserOwner(item.workflowUsername)) {
      if (workflowType === 'cic') {
        dispatch(showRtkModal(item.id));
      }
    } else if (((activeTab > 0 && activeTab < 4) || isPendingStage) && item.id != null) {
      dispatch(takeQueueItem(workflowType, item.id));
    } else if (activeTab === 4) {
      if (this.showChangeOwnershipModal(permissions, item)) {
        dispatch(showOwnershipModal({
          id: item.id,
          currentUser: item.workflowUsername,
          currentUserFullName: item.workflowUserFullName
        }));
      } else if (workflowType === 'cic') {
        const wfStatus = item.workflowStageDescription.toLowerCase();
        if (wfStatus.includes('initial') && AccessControl.hasPermission(permissions, [ActivityPerms.fwfInitialReviewer])) {
          dispatch(takeQueueItem(workflowType, item.id));
          return;
        }

        if (wfStatus.includes('secondary') && AccessControl.hasPermission(permissions, [ActivityPerms.fwfSecondaryReviewer])) {
          dispatch(takeQueueItem(workflowType, item.id));
          return;
        }

        if (wfStatus.includes('expert') && AccessControl.hasPermission(permissions, [ActivityPerms.fwfExpertReviewer])) {
          dispatch(takeQueueItem(workflowType, item.id));
          return;
        }
        dispatch(showRtkModal(item.id));
      }
    }
  }

  onDownloadExcelReportClick = () => {
    const { dispatch, workflowType } = this.props;
    if (workflowType === 'cic') {
      dispatch(getRtkXls());
    }
  }

  // only show the user column for the all tab
  get showUserColumn() {
    const { activeTab, tabs } = this.state;
    return tabs[activeTab].stage === 'all';
  }

  showChangeOwnershipModal(permissions, item) {
    const { workflowType } = this.props;

    if (item != null && item.workflowStageDescription != null && item.workflowUsername != null) {
      const wfStatus = item.workflowStageDescription.toLowerCase();
      let hasHigherPermission = false;

      if (workflowType === 'cic') {
        hasHigherPermission = AccessControl.hasPermission(permissions, [ActivityPerms.cicwfExpertReviewer, ActivityPerms.cicwfSupplReviewer]);

        if (wfStatus.includes('initial')) {
          return AccessControl.hasPermission(permissions, [ActivityPerms.cicwfInitialReviewer]) && hasHigherPermission;
        }

        if (wfStatus.includes('secondary')) {
          return AccessControl.hasPermission(permissions, [ActivityPerms.cicwfSecondaryReviewer]) && hasHigherPermission;
        }

        if (wfStatus.includes('expert')) {
          return AccessControl.hasPermission(permissions, [ActivityPerms.cicwfExpertReviewer]);
        }
      }
    }
    return false;
  }

  searchChanged(event) {
    clearTimeout(this.timer);
    const searchQuery = event.target.value;

    this.setState({ searchQuery }, () => {
      if (searchQuery.length === 0 && event.keyCode === undefined) {
        this.triggerSearch();
      }
    });
  }

  searchInGridEvent(event) {
    clearTimeout(this.timer);
    const searchQuery = event.target.value;

    if (searchQuery.length > 0 && searchQuery.length < 2) {
      return;
    }

    this.setState({ searchQuery }, () => {
      if (event.keyCode === ENTER_KEY) {
        this.triggerSearch();
      } else {
        this.timer = setTimeout(() => this.triggerSearch(), WAIT_INTERVAL);
      }
    });
  }

  triggerSearch() {
    const { activeTab, pageSize, sortBy, sortDirection, filters, searchQuery, tabs } = this.state;
    const { dispatch, workflowType } = this.props;
    const pageNumber = 1;

    this.setState({ pageNumber });

    if (activeTab === 0) {
      dispatch(getUserQueue(workflowType, pageNumber, pageSize, searchQuery));
    } else if (tabs[activeTab].stage === 'all') {
      switch (workflowType) {
        case 'cic':
          dispatch(getRtks(pageNumber, pageSize, sortBy, sortDirection, filters, searchQuery));
          break;
        default:
          break;
      }
    } else {
      dispatch(getWorkflowQueue(workflowType, pageNumber, pageSize, tabs[activeTab].stage, searchQuery));
    }
  }

  currentTabQueue() {
    const { userQueue, initialReviewQueue, secondaryReviewQueue, expertReviewQueue, allQueue } = this.props;
    const { activeTab } = this.state;

    switch (activeTab) {
      case 0:
        return userQueue;
      case 1:
        return initialReviewQueue;
      case 2:
        return secondaryReviewQueue;
      case 3:
        return expertReviewQueue;
      case 4:
        return allQueue;
      default:
        return userQueue;
    }
  }

  render() {
    const { settings, permissions, isFetchingQueues, workflowType, isGettingXls } = this.props;
    const { activeTab, tabs, pageNumber, pageSize, sortBy, sortDirection, filters } = this.state;

    const currentQueue = this.currentTabQueue();

    return (
      <div className="workflow-queues">
        <aside className="tool-panel">
          <div className="input-group">
            <input
              type="search"
              className="form-input"
              placeholder="Search..."
              ref={ (el) => { this.searchQuery = el; } }
              onKeyUp={ e => this.searchInGridEvent(e) }
              onChange={ e => this.searchChanged(e) }
            />
            <span className="input-group-addon">
              <span className="icon icon-search" />
            </span>
          </div>
          &nbsp;&nbsp;
          <AccessControl allowedPermissions={ [ActivityPerms.miDownload] }>
            <button
              type="button"
              title="Generate report"
              className="btn"
              onClick={ () => this.onDownloadExcelReportClick() }
              disabled={ isGettingXls }
            >
              <span className={ `icon ${ isGettingXls ? 'loading' : 'icon-share' } mr` } />
              <span>Generate report</span>
            </button>
          </AccessControl>
        </aside>
        <div className="workflow-tabs">
          <ul className="tab">
            { tabs != null && tabs.filter(t => t.enabled).map(tab => tab != null && (
              <li
                key={ tab.index }
                className={ `tab-item ${ tab.enabled && tab.visible && tab.index === activeTab ? 'active' : '' }` }
              >
                <button
                  type="button"
                  tabIndex={ tab.index }
                  onClick={ () => this.onSelectTab(tab.index) }
                >{ tab.title }</button>
              </li>
            )) }
          </ul>
        </div>
        <div className="tab-body">
          { isFetchingQueues && <LoadingWrapper loaded={ !isFetchingQueues } /> }
          { currentQueue && workflowType === 'cic' && <CICWorkflowQueue
            settings={ settings }
            permissions={ permissions }
            queue={ currentQueue }
            isLoading={ isFetchingQueues }
            pageNumber={ pageNumber }
            pageSize={ pageSize }
            sortBy={ sortBy }
            sortDirection={ sortDirection }
            filters={ filters }
            takeItem={ item => this.onTakeQueueItem(item) }
            changePageCallback={ (paging, sortModel, filterModel) => this.onPageChange(paging, sortModel, filterModel) }
            showUserColumn={ this.showUserColumn }
          /> }
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  if (!state.auth) {
    return {};
  }
  const {
    auth: {
      settings,
      permissions
    },
    workflow: {
      isFetchingQueues,
      userQueue,
      initialReviewQueue,
      secondaryReviewQueue,
      expertReviewQueue
    },
    rtks: {
      list,
      isListLoading,
      isRtkModalShown,
      isGettingRtkXls
    }
  } = state;
  return {
    settings,
    permissions,
    isItemShown: isRtkModalShown,
    isFetchingQueues: isFetchingQueues || isListLoading,
    userQueue,
    initialReviewQueue,
    secondaryReviewQueue,
    expertReviewQueue,
    allQueue: list,
    isGettingXls: isGettingRtkXls,
  };
};

const mapDispatchToProps = dispatch => ({ dispatch });

const WorkflowQueues = connect(
  mapStateToProps,
  mapDispatchToProps,
)(WorkflowQueuesSection);

export default WorkflowQueues;
